import { getReferenceString, MedplumClient } from '@medplum/core';
import { ExtractResource, ResourceType } from '@medplum/fhirtypes';
import { APPOINTMENT_TYPES_SYSTEM, BARIENDO_CT_SYSTEM, ProviderRole } from '@bariendo/lib/constants';
import {
  LAB_ORDER_CATEGORY,
  LAB_REVIEW_PLAN_DEFINITION_URL,
  MILESTONE_CODES,
  PREPROCEDURE_APPOINTMENT_CODES,
  PREPROCEDURE_TASK_CODES,
  SCALE_MANUFACTURER,
} from './constants';
import {
  extractProcedureAppointments,
  summarizeLabOrders,
  summarizeLabsReviewed,
  summarizeMilestones,
  summarizeNavigators,
  summarizePrescriptions,
  summarizeScales,
  summarizeTasks,
} from './summaries';
import { LookupSource, PreProcedureInputs } from './types';

const PATIENT_CHUNK = 50;
const ID_CHUNK = 100;

export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/** Every page of a search. Retool silently capped at `_count`; this does not. */
export async function searchAll<K extends ResourceType>(
  medplum: MedplumClient,
  type: K,
  params: Record<string, string>
): Promise<ExtractResource<K>[]> {
  const out: ExtractResource<K>[] = [];
  for await (const page of medplum.searchResourcePages(type, { ...params, _count: '1000' })) {
    out.push(...page);
  }
  return out;
}

/**
 * True when the signed-in user can read every resource of `type` that the page asks for. Medplum
 * refuses a search (403) on a type missing from the policy, and silently FILTERS one whose entries
 * all carry criteria (owner, group-identifier, `_compartment`) — an empty result there proves
 * nothing. So a type counts as readable only through an unrestricted entry, or an entry whose
 * criteria starts with `acceptCriteria` (a grant scoped to exactly what this page reads).
 */
export function canReadAll(medplum: MedplumClient, type: ResourceType, acceptCriteria?: string): boolean {
  const policy = medplum.getAccessPolicy();
  if (!policy) {
    return true;
  }
  const entries = (policy.resource ?? []).filter((e) => e.resourceType === type || e.resourceType === '*');
  return entries.some((e) => !e.criteria || (!!acceptCriteria && e.criteria.startsWith(acceptCriteria)));
}

/**
 * Runs one per-patient lookup so that its failure cannot take the page down. A type the user
 * can't fully read, or a search that throws, records the source as unavailable and returns empty.
 */
export async function safeLookup<T>(
  medplum: MedplumClient,
  unavailable: LookupSource[],
  source: LookupSource,
  type: ResourceType,
  run: () => Promise<T[]>,
  acceptCriteria?: string
): Promise<T[]> {
  if (!canReadAll(medplum, type, acceptCriteria)) {
    unavailable.push(source);
    return [];
  }
  try {
    return await run();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Pre-procedure lookup "${source}" failed`, err);
    unavailable.push(source);
    return [];
  }
}

/** Criteria prefix of the read-only Task grant that covers pre-procedure checklist tasks. */
export const PREPROCEDURE_TASK_CRITERIA = `Task?code=${BARIENDO_CT_SYSTEM}|`;

const patientRef = (id: string): string => getReferenceString({ resourceType: 'Patient', id });

/** Runs one search per chunk of patient references and concatenates the results. */
async function searchByPatients<K extends ResourceType>(
  medplum: MedplumClient,
  type: K,
  param: string,
  patientIds: string[],
  params: Record<string, string>
): Promise<ExtractResource<K>[]> {
  const results = await Promise.all(
    chunk(patientIds, PATIENT_CHUNK).map((ids) =>
      searchAll(medplum, type, { ...params, [param]: ids.map(patientRef).join(',') })
    )
  );
  return results.flat();
}

/** `_id` search over many ids, chunked to keep URLs short. */
async function readByIds<K extends ResourceType>(
  medplum: MedplumClient,
  type: K,
  ids: string[]
): Promise<ExtractResource<K>[]> {
  if (ids.length === 0) {
    return [];
  }
  const results = await Promise.all(
    chunk(ids, ID_CHUNK).map((group) => searchAll(medplum, type, { _id: group.join(',') }))
  );
  return results.flat();
}

const codeList = (codes: readonly string[], system: string): string => codes.map((c) => `${system}|${c}`).join(',');

function referencedIds(references: (string | undefined)[], type: string): string[] {
  const prefix = `${type}/`;
  return [
    ...new Set(references.filter((r): r is string => !!r?.startsWith(prefix)).map((r) => r.slice(prefix.length))),
  ];
}

/**
 * Loads every procedure appointment starting at or after `fromInstant` plus the per-patient data
 * the checklists need. `fromInstant` must be a full ISO instant: Medplum reads a date-only bound
 * as midnight UTC.
 */
export async function loadPreProcedureInputs(medplum: MedplumClient, fromInstant: string): Promise<PreProcedureInputs> {
  const [appointmentResources, activityDefinitions] = await Promise.all([
    searchAll(medplum, 'Appointment', {
      date: `ge${fromInstant}`,
      'status:not': 'cancelled,entered-in-error',
      'appointment-type': codeList(PREPROCEDURE_APPOINTMENT_CODES, APPOINTMENT_TYPES_SYSTEM),
      _sort: 'date',
    }),
    searchAll(medplum, 'ActivityDefinition', {
      code: codeList(PREPROCEDURE_APPOINTMENT_CODES, APPOINTMENT_TYPES_SYSTEM),
    }),
  ]);

  const appointments = extractProcedureAppointments(appointmentResources);
  const patientIds = [...new Set(appointments.map((a) => a.patientId))];

  const activityDefinitionIds: Record<string, string> = {};
  for (const ad of activityDefinitions) {
    const code = ad.code?.coding?.find((c) => c.system === APPOINTMENT_TYPES_SYSTEM)?.code;
    if (code && ad.id && !activityDefinitionIds[code]) {
      activityDefinitionIds[code] = ad.id;
    }
  }

  const unavailable: LookupSource[] = [];
  if (patientIds.length === 0) {
    return {
      appointments,
      birthDates: {},
      tasks: {},
      milestones: {},
      navigators: {},
      rx: {},
      labs: {},
      labsReviewed: {},
      scales: {},
      activityDefinitionIds,
      unavailable,
    };
  }

  const lookup = <T>(
    source: LookupSource,
    type: ResourceType,
    run: () => Promise<T[]>,
    acceptCriteria?: string
  ): Promise<T[]> => safeLookup(medplum, unavailable, source, type, run, acceptCriteria);

  const [patients, tasks, milestoneAppointments, navigatorData, medicationRequests, labOrders, labReview, devices] =
    await Promise.all([
      lookup('birthDates', 'Patient', () => readByIds(medplum, 'Patient', patientIds)),
      lookup(
        'tasks',
        'Task',
        () =>
          searchByPatients(medplum, 'Task', 'subject', patientIds, {
            code: codeList(PREPROCEDURE_TASK_CODES, BARIENDO_CT_SYSTEM),
          }),
        PREPROCEDURE_TASK_CRITERIA
      ),
      lookup('milestones', 'Appointment', () =>
        searchByPatients(medplum, 'Appointment', 'actor', patientIds, {
          'appointment-type': codeList(MILESTONE_CODES, APPOINTMENT_TYPES_SYSTEM),
          'status:not': 'cancelled,entered-in-error',
        })
      ),
      lookup('navigators', 'CareTeam', async () => {
        const [careTeams, pnRoles] = await Promise.all([
          searchByPatients(medplum, 'CareTeam', 'subject', patientIds, {}),
          searchAll(medplum, 'PractitionerRole', { role: ProviderRole.PATIENT_NAVIGATOR }),
        ]);
        const practitioners = await readByIds(
          medplum,
          'Practitioner',
          referencedIds(
            careTeams.flatMap((t) => (t.participant ?? []).map((p) => p.member?.reference)),
            'Practitioner'
          )
        );
        return [summarizeNavigators(careTeams, pnRoles, practitioners)];
      }),
      lookup('rx', 'MedicationRequest', () =>
        searchByPatients(medplum, 'MedicationRequest', 'subject', patientIds, {})
      ),
      lookup('labs', 'ServiceRequest', () =>
        searchByPatients(medplum, 'ServiceRequest', 'subject', patientIds, { category: LAB_ORDER_CATEGORY })
      ),
      lookup('labsReviewed', 'QuestionnaireResponse', async () => {
        // A failed lookup must surface as "lab reviews unavailable", not silently skip every native review.
        const plan = await medplum.searchOne('PlanDefinition', { url: LAB_REVIEW_PLAN_DEFINITION_URL });
        const planRef = plan ? getReferenceString(plan) : undefined;
        const [responses, reviewTasks] = await Promise.all([
          searchByPatients(medplum, 'QuestionnaireResponse', 'subject', patientIds, { status: 'completed' }),
          planRef
            ? searchByPatients(medplum, 'Task', 'subject', patientIds, {
                'based-on': planRef,
                status: 'completed',
              })
            : Promise.resolve([]),
        ]);
        const reviewTaskResponses = await readByIds(
          medplum,
          'QuestionnaireResponse',
          referencedIds(
            reviewTasks.map(
              (t) => t.output?.find((o) => o.type?.text === 'QuestionnaireResponse')?.valueReference?.reference
            ),
            'QuestionnaireResponse'
          )
        );
        return [summarizeLabsReviewed(responses, reviewTasks, reviewTaskResponses)];
      }),
      lookup('scales', 'Device', () =>
        searchByPatients(medplum, 'Device', 'patient', patientIds, {
          manufacturer: SCALE_MANUFACTURER,
          status: 'active',
        })
      ),
    ]);

  const birthDates: Record<string, string> = {};
  for (const p of patients) {
    if (p.id && p.birthDate) {
      birthDates[p.id] = p.birthDate;
    }
  }

  return {
    appointments,
    birthDates,
    tasks: summarizeTasks(tasks),
    milestones: summarizeMilestones(milestoneAppointments),
    navigators: navigatorData[0] ?? {},
    rx: summarizePrescriptions(medicationRequests),
    labs: summarizeLabOrders(labOrders),
    labsReviewed: labReview[0] ?? {},
    scales: summarizeScales(devices),
    activityDefinitionIds,
    unavailable,
  };
}
